import { useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Stack,
  SearchBox,
  Dropdown,
  IDropdownOption,
  IColumn,
  IconButton,
  MessageBar,
  MessageBarType,
  TooltipHost,
  Icon,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi } from '../hooks/useApi'
import { DataTable } from '../components/DataTable'
import { RiskBadge } from '../components/StatusBadge'
import type { Application, BusinessUnit, RiskLevel, SsoMode, PagedResponse } from '../types'

function healthScoreColor(score: number): string {
  if (score >= 90) return '#107c10'  // Healthy — green
  if (score >= 70) return '#0078d4'  // Fair — blue
  if (score >= 40) return '#ca5010'  // At Risk — orange
  return '#a4262c'                   // Critical — red
}

function HealthScorePill({ score }: { score: number }) {
  const color = healthScoreColor(score)
  return (
    <span
      title={`Health score: ${score}/100`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 20,
        borderRadius: 10,
        background: color,
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      {score}
    </span>
  )
}

export function Applications() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const hasOwnerParam = searchParams.get('hasOwner')
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<number | undefined>()
  const [selectedRiskLevel, setSelectedRiskLevel] = useState<number | undefined>()
  // null = all, true = with owner, false = unowned
  const [hasOwner, setHasOwner] = useState<boolean | null>(
    hasOwnerParam === 'true' ? true : hasOwnerParam === 'false' ? false : null
  )
  const [ssoMode, setSsoMode] = useState<string | undefined>(
    searchParams.get('ssoMode') || undefined
  )
  const [page, setPage] = useState(1)
  const pageSize = 25

  const fetchApplications = useCallback(
    () => apiService.getApplications({
      page,
      pageSize,
      search: searchQuery || undefined,
      businessUnitId: selectedBusinessUnit,
      riskLevelId: selectedRiskLevel,
      hasOwner: hasOwner !== null ? hasOwner : undefined,
      ssoMode,
    }),
    [page, pageSize, searchQuery, selectedBusinessUnit, selectedRiskLevel, hasOwner, ssoMode]
  )

  const fetchBusinessUnits = useCallback(() => apiService.getBusinessUnits(), [])
  const fetchRiskLevels = useCallback(() => apiService.getRiskLevels(), [])
  const fetchSsoModes = useCallback(() => apiService.getSsoModes(), [])

  const {
    data: applications,
    loading: appsLoading,
    error: appsError,
  } = useApi<PagedResponse<Application>>(fetchApplications, [page, searchQuery, selectedBusinessUnit, selectedRiskLevel, hasOwner, ssoMode])

  const { data: businessUnits } = useApi<BusinessUnit[]>(fetchBusinessUnits)
  const { data: riskLevels } = useApi<RiskLevel[]>(fetchRiskLevels)
  const { data: ssoModes } = useApi<SsoMode[]>(fetchSsoModes)

  const businessUnitOptions: IDropdownOption[] = [
    { key: '', text: 'All Business Units' },
    ...(businessUnits?.map(bu => ({ key: bu.businessUnitId, text: bu.name })) || []),
  ]

  const riskLevelOptions: IDropdownOption[] = [
    { key: '', text: 'All Risk Levels' },
    ...(riskLevels?.map(rl => ({ key: rl.riskLevelId, text: rl.name })) || []),
  ]

  const ownerOptions: IDropdownOption[] = [
    { key: '', text: 'All' },
    { key: 'true', text: 'With Owner' },
    { key: 'false', text: 'Unowned' },
  ]

  const ssoModeOptions: IDropdownOption[] = [
    { key: '', text: 'All SSO Modes' },
    ...(Array.isArray(ssoModes) ? ssoModes.map(m => ({ key: m.name, text: m.displayName })) : []),
  ]

  const healthLevelTooltip = `Health Level is determined by:
- Has an assigned owner
- Has an assigned business unit
- No expired credentials
- No credentials expiring within 30 days`

  const columns: IColumn[] = [
    {
      key: 'displayName',
      name: 'Application',
      fieldName: 'displayName',
      minWidth: 250,
      onRender: (item: Application) => (
        <Stack title={`${item.displayName}\n${item.entraAppId}`}>
          <span style={{ fontWeight: 500 }}>{item.displayName}</span>
          <span style={{ fontSize: 12, color: '#605e5c' }}>{item.entraAppId}</span>
        </Stack>
      ),
    },
    {
      key: 'businessUnit',
      name: 'Business Unit',
      fieldName: 'businessUnitName',
      minWidth: 150,
      onRender: (item: Application) => {
        const value = item.businessUnitName || '-'
        return <span title={value}>{value}</span>
      },
    },
    {
      key: 'healthLevel',
      name: 'Health Level',
      fieldName: 'riskLevelName',
      minWidth: 120,
      onRenderHeader: () => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
          <span>Health Level</span>
          <TooltipHost content={healthLevelTooltip}>
            <Icon iconName="Info" styles={{ root: { fontSize: 12, color: '#605e5c', cursor: 'help' } }} />
          </TooltipHost>
        </Stack>
      ),
      onRender: (item: Application) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          {item.riskLevelName && item.riskLevelColor ? (
            <RiskBadge name={item.riskLevelName} color={item.riskLevelColor} />
          ) : (
            <span>-</span>
          )}
          {item.healthScore != null && (
            <HealthScorePill score={item.healthScore} />
          )}
        </Stack>
      ),
    },
    {
      key: 'owner',
      name: 'Primary Owner',
      fieldName: 'primaryOwnerEmail',
      minWidth: 180,
      onRender: (item: Application) => {
        const value = item.primaryOwnerEmail || 'Unassigned'
        return <span title={value}>{value}</span>
      },
    },
    {
      key: 'credentials',
      name: 'Credentials',
      minWidth: 150,
      onRender: (item: Application) => (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <span>{item.credentialCount} total</span>
          {item.expiredCredentialCount > 0 && (
            <span style={{ color: '#a4262c', fontWeight: 500 }}>
              {item.expiredCredentialCount} expired
            </span>
          )}
          {item.expiringCredentialCount > 0 && item.expiredCredentialCount === 0 && (
            <span style={{ color: '#ffaa44', fontWeight: 500 }}>
              {item.expiringCredentialCount} expiring
            </span>
          )}
        </Stack>
      ),
    },
    {
      key: 'actions',
      name: '',
      minWidth: 50,
      maxWidth: 50,
      isResizable: false,
      onRender: (item: Application) => (
        <IconButton
          iconProps={{ iconName: 'ChevronRight' }}
          title="View Details"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/applications/${item.applicationId}`)
          }}
        />
      ),
    },
  ]

  const handleSearch = (newValue?: string) => {
    setSearchQuery(newValue || '')
    setPage(1)
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Applications</h1>
        <p className="page-description">
          Manage Entra ID application registrations and their credential assignments
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
          <SearchBox
            placeholder="Search applications..."
            value={searchQuery}
            onChange={(_, newValue) => handleSearch(newValue)}
            styles={{ root: { width: 300 } }}
          />
          <Dropdown
            placeholder="Business Unit"
            ariaLabel="Filter by Business Unit"
            options={businessUnitOptions}
            selectedKey={selectedBusinessUnit || ''}
            onChange={(_, option) => {
              setSelectedBusinessUnit(option?.key ? Number(option.key) : undefined)
              setPage(1)
            }}
            styles={{ root: { width: 200 } }}
          />
          <Dropdown
            placeholder="Risk Level"
            ariaLabel="Filter by Risk Level"
            options={riskLevelOptions}
            selectedKey={selectedRiskLevel || ''}
            onChange={(_, option) => {
              setSelectedRiskLevel(option?.key ? Number(option.key) : undefined)
              setPage(1)
            }}
            styles={{ root: { width: 180 } }}
          />
          <Dropdown
            placeholder="Owner"
            ariaLabel="Filter by owner presence"
            options={ownerOptions}
            selectedKey={hasOwner === null ? '' : String(hasOwner)}
            onChange={(_, option) => {
              const val = option?.key
              setHasOwner(val === 'true' ? true : val === 'false' ? false : null)
              setPage(1)
            }}
            styles={{ root: { width: 150 } }}
          />
          <Dropdown
            placeholder="SSO Mode"
            ariaLabel="Filter by SSO mode"
            options={ssoModeOptions}
            selectedKey={ssoMode || ''}
            onChange={(_, option) => {
              setSsoMode(option?.key ? String(option.key) : undefined)
              setPage(1)
            }}
            styles={{ root: { width: 160 } }}
          />
        </Stack>
      </div>

      {/* Results */}
      {appsError && (
        <MessageBar
          messageBarType={MessageBarType.error}
          styles={{ root: { marginBottom: 16 } }}
        >
          {appsError}
        </MessageBar>
      )}

      <div className="card">
        <DataTable
          items={applications?.items || []}
          columns={columns}
          loading={appsLoading}
          error={null}
          emptyMessage="No applications found"
          onItemClick={(item) => navigate(`/applications/${item.applicationId}`)}
        />

        {/* Pagination */}
        {applications && applications.totalPages > 1 && (
          <Stack
            horizontal
            horizontalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { marginTop: 16 } }}
          >
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            />
            <span style={{ padding: '8px 16px' }}>
              Page {page} of {applications.totalPages}
            </span>
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              disabled={page === applications.totalPages}
              onClick={() => setPage(p => p + 1)}
            />
          </Stack>
        )}
      </div>
    </div>
  )
}
